import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import Switch from "common/Buttons/Switch";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import { OrderTypes } from "@shared/features/trades/common/types";
import TrailingStop from "features/tradingTerminal/Trailing/TrailingStop";
import TrailingEntryAndTakeProfit from "features/tradingTerminal/Trailing/TrailingEntryAndTakeProfit";
import React, { CSSProperties, useContext } from "react";
import styled, { useTheme } from "styled-components";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import OverrideTypeToggle from "features/bots/BotForm/common/OverrideTypeToggle.tsx";
import { OverrideType } from "@shared/features/bots/BotForm/common/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ORDER_TYPE_TO_TRAILING_TITLE } from "@shared/features/tradingTerminal/common/constants.ts";

interface TrailingSectionProps {
  orderType: OrderTypes;
  titleStyle?: CSSProperties;
  titleRowStyle?: CSSProperties;
  style?: CSSProperties;
}

const TrailingSection = ({
  orderType,
  titleStyle,
  titleRowStyle,
  style,
}: TrailingSectionProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { useTrailing, setUseTrailing, isSignal } = useContext(
    TradingTerminalContext
  )!;
  const { isNewLook } = useTheme();

  const getToggle = () => {
    const toggleStyle = { marginLeft: 12 };
    const checked = useTrailing[orderType];
    if (isSignal) {
      return (
        <OverrideTypeToggle
          onClick={(overrideType) => {
            setUseTrailing(orderType, overrideType === OverrideType.PERSONAL);
          }}
          options={[OverrideType.USER_CONFIGURATION, OverrideType.PERSONAL]}
          style={toggleStyle}
          selectedOption={
            checked ? OverrideType.PERSONAL : OverrideType.USER_CONFIGURATION
          }
          showPersonalAsOn={true}
        />
      );
    }
    return (
      <Switch
        checked={checked}
        onClick={() => setUseTrailing(orderType, !useTrailing[orderType])}
        size="small"
        style={toggleStyle}
      />
    );
  };

  const content =
    orderType === OrderTypes.STOP ? (
      <TrailingStop />
    ) : (
      <TrailingEntryAndTakeProfit orderType={orderType} />
    );

  return (
    <div style={style}>
      <Flex style={{ width: "100%", ...titleRowStyle }}>
        <Text style={titleStyle}>
          {t(ORDER_TYPE_TO_TRAILING_TITLE[orderType])}
        </Text>
        {getToggle()}
      </Flex>
      <ExpandCollapse expanded={useTrailing[orderType]} overflowVisible={true}>
        {isNewLook ? <NewLookBody>{content}</NewLookBody> : content}
      </ExpandCollapse>
    </div>
  );
};

export default TrailingSection;

/* New look: the space under the title row lives inside the collapsible part,
   so a closed section ends right at its switch. */
const NewLookBody = styled.div`
  padding-top: 12px;
`;
